import type React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  rowApproveMetadataSchema,
  rowEditMetadataSchema,
  type AuditLog,
  type Row,
} from '@tekvoice/shared';
import { BiText } from '@/components/bi-text';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { formatDateTime } from '@/lib/utils';

const ACTION_LABELS: Record<string, string> = {
  'row.edit': 'Text edited',
  'row.override': 'Pronunciation override changed',
  'row.approve': 'Approved & published',
  'row.unapprove': 'Un-approved',
  'row.create': 'Row created',
  'row.remove': 'Row removed',
  'asset.generate': 'Audio generated',
  'asset.regenerate': 'Audio regenerated',
  'asset.select_take': 'Take selected',
};

function stringField(metadata: Record<string, unknown>, key: string): string | null {
  const value = metadata[key];
  return typeof value === 'string' ? value : null;
}

function HistoryEntry({ entry }: { entry: AuditLog }): React.JSX.Element {
  const edit = rowEditMetadataSchema.safeParse(entry.metadata);
  const approve = rowApproveMetadataSchema.safeParse(entry.metadata);
  const metadata = entry.metadata as Record<string, unknown>;

  return (
    <li className="border-l-2 border-muted pl-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="secondary">{ACTION_LABELS[entry.action] ?? entry.action}</Badge>
        <span className="text-xs text-muted-foreground">{formatDateTime(entry.createdAt)}</span>
        <span className="text-xs text-muted-foreground">
          by {entry.actorName ?? entry.actorEmail ?? 'system'}
        </span>
      </div>

      {edit.success ? (
        <div className="mt-2 space-y-1 text-sm">
          {(['englishText', 'arabicText'] as const).map((field) =>
            edit.data.before[field] !== edit.data.after[field] ? (
              <div key={field} className="rounded-md bg-muted/50 p-2">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  {field === 'englishText' ? 'English' : 'Arabic'}
                </div>
                <BiText as="div" className="text-destructive line-through">
                  {edit.data.before[field]}
                </BiText>
                <BiText as="div" className="text-emerald-700">
                  {edit.data.after[field]}
                </BiText>
              </div>
            ) : null,
          )}
          {edit.data.staleLanguages.length > 0 ? (
            <p className="text-xs text-amber-700">
              made stale: {edit.data.staleLanguages.join(', ')}
            </p>
          ) : null}
        </div>
      ) : approve.success ? (
        <div className="mt-2 space-y-1 text-xs text-muted-foreground">
          <p>
            slug <code className="rounded bg-muted px-1">{approve.data.publishedSlug}</code>
          </p>
          {approve.data.paths.map((path) => (
            <p key={`${path.language}-${path.publishedPath}`} className="break-all">
              {path.language}: {path.deliveredPath ?? path.publishedPath} · generation{' '}
              {path.generationNew}
              {path.archivedPath ? ` · archived ${path.archivedPath}` : ''}
            </p>
          ))}
        </div>
      ) : entry.action === 'asset.generate' || entry.action === 'asset.regenerate' ? (
        <p className="mt-2 text-xs text-muted-foreground">
          {stringField(metadata, 'language') ?? '—'} · voice{' '}
          {stringField(metadata, 'voiceId') ?? '—'} · model {stringField(metadata, 'modelId') ?? '—'}
        </p>
      ) : (
        <p className="mt-2 break-all text-xs text-muted-foreground">{JSON.stringify(entry.metadata)}</p>
      )}
    </li>
  );
}

interface RowHistoryDrawerProps {
  row: Row | null;
  onClose: () => void;
}

/** SPEC §10 — per-row history: edits, generations, approvals. */
export function RowHistoryDrawer({ row, onClose }: RowHistoryDrawerProps): React.JSX.Element {
  const history = useQuery({
    queryKey: ['row', row?.id, 'history'],
    queryFn: () => api.rows.history(row?.id ?? ''),
    enabled: row !== null,
  });

  return (
    <Sheet
      open={row !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Row {row ? row.rowIndex + 1 : ''} history</SheetTitle>
          <SheetDescription>
            Every edit, generation and approval recorded for this row.
          </SheetDescription>
        </SheetHeader>

        {history.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : (history.data?.items.length ?? 0) === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nothing recorded for this row yet — it appears here as soon as it is edited, generated or
            approved.
          </p>
        ) : (
          <ul className="space-y-4">
            {(history.data?.items ?? []).map((entry) => (
              <HistoryEntry key={entry.id} entry={entry} />
            ))}
          </ul>
        )}
      </SheetContent>
    </Sheet>
  );
}
